import { contentReferencesAreEqual } from '@gitbook/app-content-refs';

import {
    type GitFileState,
    type PageLayout,
    type RevisionPage,
    type RevisionPageDocumentCover,
    type RevisionPageMetaLinks,
    RevisionPageType,
    type RevisionVariables,
} from '../types';
import { normalizeLayoutOptionsWithDefaults } from './utils';

export type ComparePageOptions = {
    /**
     * Default true. If false, changes to git file state or variables will be ignored.
     */
    compareMetadata?: boolean;
};

/**
 * Quick compare two pages to determine if there are changes.
 * Return true if it's modified.
 */
export function comparePages(
    p1: RevisionPage,
    p2: RevisionPage,
    options: ComparePageOptions = {}
): boolean {
    const { compareMetadata = true } = options;

    if (p1.type !== p2.type || p1.id !== p2.id) {
        return true;
    }

    if (p1.type === RevisionPageType.Group && p2.type === RevisionPageType.Group) {
        return (
            p1.slug !== p2.slug ||
            p1.title !== p2.title ||
            p1.emoji !== p2.emoji ||
            p1.icon !== p2.icon ||
            !!p1.hidden !== !!p2.hidden ||
            !!p1.noIndex !== !!p2.noIndex ||
            !!p1.noRobotsIndex !== !!p2.noRobotsIndex ||
            p1.condition !== p2.condition
        );
    }
    if (p1.type === RevisionPageType.Document && p2.type === RevisionPageType.Document) {
        return (
            p1.slug !== p2.slug ||
            p1.title !== p2.title ||
            p1.linkTitle !== p2.linkTitle ||
            p1.document !== p2.document ||
            p1.description !== p2.description ||
            p1.emoji !== p2.emoji ||
            p1.icon !== p2.icon ||
            !!p1.hidden !== !!p2.hidden ||
            !!p1.noIndex !== !!p2.noIndex ||
            !!p1.noRobotsIndex !== !!p2.noRobotsIndex ||
            p1.condition !== p2.condition ||
            comparePageCovers(p1.cover, p2.cover) ||
            (compareMetadata && comparePageVariables(p1.variables, p2.variables)) ||
            !comparePageLayouts(p1.layout, p2.layout) ||
            comparePageMetaLinks(p1.metaLinks, p2.metaLinks) ||
            (compareMetadata && compareGitFileState(p1.git, p2.git))
        );
    }
    if (p1.type === RevisionPageType.Link && p2.type === RevisionPageType.Link) {
        return (
            p1.title !== p2.title ||
            p1.emoji !== p2.emoji ||
            p1.icon !== p2.icon ||
            !contentReferencesAreEqual(p1.target, p2.target) ||
            p1.condition !== p2.condition
        );
    }

    return true;
}

/**
 * Compare two page layouts. Return true if they are the same.
 */
export function comparePageLayouts(
    layoutP1: PageLayout | undefined,
    layoutP2: PageLayout | undefined
): boolean {
    if (typeof layoutP1 === 'object' && typeof layoutP2 === 'object') {
        const p1 = normalizeLayoutOptionsWithDefaults(layoutP1);
        const p2 = normalizeLayoutOptionsWithDefaults(layoutP2);
        const p1Keys = Object.keys(p1);
        const p2Keys = Object.keys(p2);
        return p1Keys.length === p2Keys.length && p1Keys.every((key) => p1?.[key] === p2?.[key]);
    }
    return layoutP1 === layoutP2;
}

/**
 * Compare two page variables. Return true if it's modified.
 */
export function comparePageVariables(
    variablesP1: RevisionVariables | undefined,
    variablesP2: RevisionVariables | undefined
): boolean {
    if (typeof variablesP1 === 'object' && typeof variablesP2 === 'object') {
        const p1 = variablesP1 ?? {};
        const p2 = variablesP2 ?? {};
        const p1Keys = Object.keys(p1);
        const p2Keys = Object.keys(p2);
        return p1Keys.length !== p2Keys.length || !p1Keys.every((key) => p1?.[key] === p2?.[key]);
    }
    return variablesP1 !== variablesP2;
}

/**
 * Compare two page covers. Return true if they are different.
 */
export function comparePageCovers(
    p1: RevisionPageDocumentCover | undefined,
    p2: RevisionPageDocumentCover | undefined
): boolean {
    if (!p1 && !p2) {
        return false;
    }
    if (!p1 || !p2) {
        return true;
    }

    const lightRefIsDifferent = !contentReferencesAreEqual(p1.ref, p2.ref);
    const darkRefIsDifferent = !contentReferencesAreEqual(p1.refDark, p2.refDark);
    return (
        p1.yPos !== p2.yPos || p1.height !== p2.height || lightRefIsDifferent || darkRefIsDifferent
    );
}

/**
 * Returns true if different.
 */
function compareGitFileState(p1: GitFileState | undefined, p2: GitFileState | undefined): boolean {
    if (!p1 && !p2) {
        return false;
    }

    if (!p1 || !p2) {
        return true;
    }

    return p1.filePath !== p2.filePath || p1.oid !== p2.oid;
}

/**
 * Returns true if different.
 */
export function comparePageMetaLinks(
    p1: RevisionPageMetaLinks | undefined,
    p2: RevisionPageMetaLinks | undefined
): boolean {
    if (!p1 && !p2) {
        return false;
    }

    if (!p1 || !p2) {
        return true;
    }

    const p1Canonical = p1.canonical;
    const p2Canonical = p2.canonical;
    const canonicalIsDifferent = !contentReferencesAreEqual(p1Canonical, p2Canonical);

    const p1Alternates = p1.alternates ?? [];
    const p2Alternates = p2.alternates ?? [];

    if (p1Alternates.length !== p2Alternates.length) {
        return true;
    }

    const alternatesAreDifferent = p1Alternates.some((alt1, index) => {
        const alt2 = p2Alternates[index];
        return !contentReferencesAreEqual(alt1, alt2);
    });

    return canonicalIsDifferent || alternatesAreDifferent;
}
